import { Component, ChangeDetectionStrategy, inject, signal, computed, input, output, viewChild, ElementRef, HostListener } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgClass } from '@angular/common';
import { debounceTime, distinctUntilChanged, filter, forkJoin, map, of, switchMap, tap } from 'rxjs';

import { environment } from '../../environments/environment';
import { TenantService } from '../services/tenant.service';
import { StudentsService } from '../admin/manage-students/students.service';
import { LoadingService } from '../shared/services/loading.service';
import { SafeHtmlPipe } from '../shared/pipes/safe-html.pipe';
import { SearchIconSvgComponent } from '../svg-sprites/general/search-icon-svg.component';
import { User } from '../models/user.model';

@Component({
  selector: 'search',
  standalone: true,
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatAutocompleteModule, MatOptionModule, MatProgressSpinnerModule, 
    MatTooltipModule, NgClass, SafeHtmlPipe, SearchIconSvgComponent
  ]
})
export class SearchComponent {
  // --- 1. Services ---
  private readonly tenant = inject(TenantService);
  private readonly http = inject(HttpClient);
  private readonly studentsService = inject(StudentsService);
  private readonly loading = inject(LoadingService);

  // --- 2. Inputs & Outputs ---
  readonly indexes = input<string[]>([]);
  readonly placeholder = input('');
  readonly searchIconMarginTop = input('12px');
  readonly optionSelected = output<any>();

  // --- 3. View Queries ---
  readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('search');
  readonly autocompleteTrigger = viewChild(MatAutocompleteTrigger);

  // --- 4. State Signals ---
  readonly query = signal('');
  readonly initialSearchLoading = signal(false);
  readonly minSearchLength = 4;

  // --- 5. Computed & Reactive Data ---
  readonly isUserSearch = computed(() => this.indexes().includes('users'));

  // The "Search Engine": This replaces ngAfterViewInit + fromEvent
  readonly searchOptions = toSignal(
    toObservable(this.query).pipe(
      debounceTime(500),
      distinctUntilChanged(),
      filter(q => q.length >= this.minSearchLength),
      tap(() => this.initialSearchLoading.set(true)),
      switchMap(q => this.performSearch(q)),
      tap(() => this.initialSearchLoading.set(false))
    ),
    { initialValue: [] }
  );

  readonly noMatchingRecord = computed(() => 
    this.query().length >= this.minSearchLength && 
    !this.initialSearchLoading() && 
    this.searchOptions().length === 0
  );

  // --- 6. Methods ---

  private performSearch(q: string) {
    const tenantId = this.tenant.id;
    const requests = this.indexes()
      .map(index => this.getSearchUrl(index))
      .filter(url => !!url)
      .map(url => this.http.get<any>(url!, { params: { q, tenantId } }));

    if (requests.length === 0) return of([]);

    return forkJoin(requests).pipe(
      map(responses => this.flattenResults(responses))
    );
  }

  private flattenResults(responses: any[]): any[] {
    return responses.flatMap(resp => {
      // Handle the different shapes of OpenSearch responses
      if (resp.hits) return resp.hits;
      return resp.results?.flatMap((r: any) => r.hits) || [];
    }).filter(h => !h.email?.includes('onlinecoursehost'));
  }

  private getSearchUrl(indexName: string): string | null {
    const mapping: Record<string, string> = {
      'lessons': environment.openSearch.searchLessonsUrl,
      'questions_and_answers': environment.openSearch.searchQandaUrl,
      'users': environment.openSearch.searchUsersUrl
    };
    return mapping[indexName] || null;
  }

  async onSearchOptionSelected(event: MatAutocompleteSelectedEvent) {
    const selection = event.option.value;
    
    if (this.isUserSearch()) {
      const studentId = selection?.objectID || selection?.id;
      if (studentId) {
        try {
          this.loading.loadingOn();
          const user = await this.studentsService.getUserByTenantAndId(studentId);
          this.optionSelected.emit(user);
        } catch (error) {
          this.optionSelected.emit(selection);
        } finally {
          this.loading.loadingOff();
        }
      }
    } else {
      this.optionSelected.emit(selection);
    }

    // Reset UI state
    this.query.set('');
    if (this.searchInput()) {
      this.searchInput()!.nativeElement.value = '';
    }
  }

  findHtml(option: any): string {
    const res = option?._highlightResult;
    return res?.title?.value || res?.text?.value || res?.displayName?.value || res?.email?.value || "";
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    const inputEl = this.searchInput()?.nativeElement;
    const trigger = this.autocompleteTrigger();
    if (!inputEl || !trigger?.panelOpen) return;

    const target = event.target as HTMLElement;
    if (!inputEl.contains(target) && !target.closest('.mat-mdc-autocomplete-panel')) {
      trigger.closePanel();
    }
  }

  // --- 7. Utility ---
  syncPanelWidthToInput(): void {
    const inputEl = this.searchInput()?.nativeElement;
    const trigger = this.autocompleteTrigger();
    if (!inputEl || !trigger) return;
    
    const widthPx = inputEl.getBoundingClientRect().width;
    // Use requestAnimationFrame for smoother UI updates than setTimeout(0)
    requestAnimationFrame(() => {
      const host = (trigger as any)._overlayRef?.overlayElement;
      if (host) host.style.width = `${widthPx}px`;
    });
  }
}